import { z } from "zod/v4";
import { parseCopyExamplesContent } from "@hightouch/core/content/lcm-copy-examples/schema";
import { DagSchema } from "../../../../dag-skills/content";
import {
  AGENT_SCOPES,
  type AgenticSettingsCopyExamplesContent,
  AgenticSettingsEntityType,
  type AgenticSettingsContextMarkdownContent,
  AgenticSettingsReviewOpType,
  ContextFileType,
  PROPOSED_ENTITY_KEY_PATTERN,
} from "../../types";

// The entity operations as the agent writes them (wire) and as the operation jsonb stores
// them. packages/app compiles this through the llm/schema barrel, so keep it free of
// database, storage, and other node-only imports.

const ContextReviewMarkdownContentSchema = z.object({
  markdown: z.string(),
  dag: DagSchema.optional().describe(
    "For a skill written as steps (a DAG skill): the whole steps structure. Its markdown is generated from it, so send markdown as an empty string. Omit for any other doc.",
  ),
});

// The same enums the stored metadata uses, so the wire cannot widen them.
const ContextReviewContextTypeSchema = z
  .enum(ContextFileType)
  .nullable()
  .optional()
  .describe(
    "For context_markdown / context_file only: the doc's contextType per the standards (business_context and brand_guidelines are always-read; everything else is routed by name + description, so omit it). On an edit, omit to keep the current value and send null to clear it.",
  );
// An empty array is coerced to "every agent" at apply, never persisted as-is.
const ContextReviewAgentScopesSchema = z
  .array(z.enum(AGENT_SCOPES))
  .nullable()
  .optional()
  .describe(
    "The agents the doc or style is routed to (ads, lcm, data) per the standards' manifest. Omit or null when it applies to every agent. On an edit, omit to keep the current value and send null to widen to all agents.",
  );
const ContextReviewBrandProfileIdsSchema = z
  .array(z.string().min(1))
  .nullable()
  .optional();

export const ENTITY_WIRE_OPERATIONS = [
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.DeleteEntity),
  }),
  // `edit_entity` collapses both entity_types into one branch because AJV rejects
  // discriminator-union variants that share a discriminator value (see
  // contextReviewSuggestionSchema in llm/schema). entityValidator in ./handler.ts enforces
  // the per-entity_type field rules (no `content` for context_file edits, etc.).
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.EditEntity),
    // copy_references is intentionally excluded: those entities must never be authored through
    // the generic context suggestions path (they bypass the dedicated copy_examples_review pipeline's
    // guarantees).
    entity_type: z.enum([
      AgenticSettingsEntityType.ContextMarkdown,
      AgenticSettingsEntityType.ContextFile,
      AgenticSettingsEntityType.Style,
    ]),
    // name and description are non-nullable. Omit to leave unchanged; never send null or
    // an empty string — descriptions are what the agent uses to decide whether to load
    // an on-demand doc, so clearing one is a real regression.
    name: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
    // context_markdown and style only (a style is a markdown document) — enforced at the
    // app layer.
    content: ContextReviewMarkdownContentSchema.optional(),
    context_type: ContextReviewContextTypeSchema,
    agent_scopes: ContextReviewAgentScopesSchema,
    brand_profile_ids: ContextReviewBrandProfileIdsSchema.describe(
      "Only when the user asked to move or share this doc between brand profiles: the profiles it should apply to (ids verbatim from list_brand_profiles), replacing the current assignment. Omit to keep the current assignment; null to make it apply to every profile.",
    ),
  }),
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.AddEntity),
    // content xor file_id, keyed on entity_type; entityValidator enforces it (collapsed
    // into one op_type branch for the same AJV reason as edit). copy_references is
    // intentionally excluded (see edit_entity above).
    entity_type: z.enum([
      AgenticSettingsEntityType.ContextMarkdown,
      AgenticSettingsEntityType.ContextFile,
      AgenticSettingsEntityType.Style,
    ]),
    name: z.string().min(1),
    // Required — new docs/files without a description are invisible to on-demand loading,
    // and a new style's description is its selection instructions.
    description: z.string().min(1),
    // Stable identity for this proposal across turns, chosen by you, not the server: a slug
    // derived from `name` so you can reproduce it later from the same name. Emitting the same
    // proposed_entity_key again REPLACES your earlier proposal instead of adding a second one
    // — that is how you revise a proposal the user asked you to change.
    //
    // Constrained to a slug on the schema itself, not in a hand-rolled check, so the bound
    // reaches the model as a JSON Schema `pattern` and the runtime's AJV pass rejects a
    // violation before it can be persisted. The key keys revision chains, is shown in the UI,
    // and is a candidate filename if pending proposals are ever staged to disk.
    proposed_entity_key: z
      .string()
      .regex(
        PROPOSED_ENTITY_KEY_PATTERN,
        "proposed_entity_key must be a slug of lowercase letters, digits and hyphens (max 64 chars), starting with a letter or digit",
      )
      .describe(
        "Stable slug identifying this proposed doc across turns, derived from its name (e.g. 'brand-voice-guidelines'). Reuse the SAME proposed_entity_key to revise a proposal you already made in this session; use a new one for a genuinely new doc. Lowercase letters, digits and hyphens only.",
      ),
    // context_markdown and style: a style is a markdown document whose image lines cite
    // reference entities minted by save_reference_images.
    content: ContextReviewMarkdownContentSchema.optional(),
    // context_file only — the uploaded file's id; never inline file bytes.
    file_id: z
      .string()
      .min(1)
      .optional()
      .describe(
        "For entity_type=context_file only: the File ID (from the <uploaded_files> block) of the uploaded file to add to context. Do not include file contents.",
      ),
    // Every add type.
    brand_profile_ids: ContextReviewBrandProfileIdsSchema.describe(
      "The brand profiles this doc or style applies to (ids verbatim from list_brand_profiles): a profile the user named, or the profile that best fits these uploads and brand-profile suggestion. Omit or null when it applies to all profiles.",
    ),
    // context_type is context_markdown / context_file only; agent_scopes applies to
    // styles too — enforced at the app layer.
    context_type: ContextReviewContextTypeSchema,
    agent_scopes: ContextReviewAgentScopesSchema,
  }),
] as const;

const markdownContentSchema = z.object({
  markdown: z.string(),
  dag: z.unknown().optional(),
}) satisfies z.ZodType<AgenticSettingsContextMarkdownContent>;
const contextTypeSchema = z.enum(ContextFileType);
const agentScopeSchema = z.enum(AGENT_SCOPES);

// Typed by its output: a transform reads as an optional key to the non-strict backend
// compiler, and the content is required at runtime.
const copyExamplesReviewContentSchema: z.ZodType<
  AgenticSettingsCopyExamplesContent,
  unknown
> = z
  .object({})
  .passthrough()
  .transform(
    (value): AgenticSettingsCopyExamplesContent =>
      parseCopyExamplesContent(value),
  );

const brandProfileIdsSchema = z.array(z.string().min(1)).nullish();

// Stored leniently: the apply path coerces an empty agent_scopes to null. A style has no
// context type; agent_scopes (null for every agent) and brand profiles classify it.
const styleScopingSchema = {
  agent_scopes: z.array(agentScopeSchema).nullish(),
  brand_profile_ids: brandProfileIdsSchema,
};
const docScopingSchema = {
  context_type: contextTypeSchema.nullish(),
  ...styleScopingSchema,
};

// AgenticSettingsReviewOperation is inferred from these schemas. An edit omits name or
// description to keep it but cannot clear it, since descriptions decide on-demand loading.
const editEntityOperationSchema = z.discriminatedUnion("entity_type", [
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.EditEntity),
    entity_type: z.literal(AgenticSettingsEntityType.ContextMarkdown),
    name: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
    content: markdownContentSchema.optional(),
    ...docScopingSchema,
  }),
  // A file's content is never edited through review, so its edit carries metadata only.
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.EditEntity),
    entity_type: z.literal(AgenticSettingsEntityType.ContextFile),
    name: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
    ...docScopingSchema,
  }),
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.EditEntity),
    entity_type: z.literal(AgenticSettingsEntityType.CopyReferences),
    name: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
    content: copyExamplesReviewContentSchema.optional(),
  }),
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.EditEntity),
    entity_type: z.literal(AgenticSettingsEntityType.Style),
    name: z.string().min(1).optional(),
    description: z.string().min(1).optional(),
    content: markdownContentSchema.optional(),
    ...styleScopingSchema,
  }),
]);

// add_entity carries an entity_type-discriminated payload: markdown adds carry inline
// content; file adds carry a `file_id` reference resolved to durable storage at apply time.
const addEntityOperationSchema = z.discriminatedUnion("entity_type", [
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.AddEntity),
    entity_type: z.literal(AgenticSettingsEntityType.ContextMarkdown),
    name: z.string().min(1),
    description: z.string().min(1),
    content: markdownContentSchema,
    ...docScopingSchema,
  }),
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.AddEntity),
    entity_type: z.literal(AgenticSettingsEntityType.ContextFile),
    name: z.string().min(1),
    description: z.string().min(1),
    // The LLMChatMessageFile.fileId from the agent's <uploaded_files> block. Apply promotes
    // that upload; file bytes never ride the operation, as they would blow the output budget.
    file_id: z.string().min(1),
    ...docScopingSchema,
  }),
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.AddEntity),
    entity_type: z.literal(AgenticSettingsEntityType.CopyReferences),
    name: z.string().min(1),
    description: z.string().min(1),
    content: copyExamplesReviewContentSchema,
    // Set server-side, never by the agent, when the originating session was scoped to a
    // brand profile. Accept assigns the new entity to it; absent or null applies to all.
    brand_profile_id: z.string().min(1).nullish(),
  }),
  z.object({
    op_type: z.literal(AgenticSettingsReviewOpType.AddEntity),
    entity_type: z.literal(AgenticSettingsEntityType.Style),
    name: z.string().min(1),
    // Required at the op level (the entity column is nullable): a style's description is
    // its loading/selection instructions.
    description: z.string().min(1),
    content: markdownContentSchema,
    ...styleScopingSchema,
  }),
]);

export const ENTITY_STORED_OPERATIONS = [
  z.object({ op_type: z.literal(AgenticSettingsReviewOpType.DeleteEntity) }),
  editEntityOperationSchema,
  addEntityOperationSchema,
] as const;
